import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown, Globe, Mail } from "lucide-react";
import api, { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import NatureRomantique from "./themes/NatureRomantique";
import MadrasEclatant from "./themes/MadrasEclatant";
import HeritageChic from "./themes/HeritageChic";
import BohemeAubePoudree from "./themes/BohemeAubePoudree";
import EpureContemporaine from "./themes/EpureContemporaine";

const THEMES = {
  chic: {
    bg: "#fcf9f8",
    surface: "#FFFFFF",
    surface2: "#F4F1EA",
    text: "#1b1c1c",
    soft: "#5C5C5C",
    accent: "#B89253",
    accentSoft: "#d1948b",
    border: "#dcd9d9",
    headingFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "/images/chic_wedding_hero.png",
    accentImage: "/images/chic_wedding_hero.png",
  },
  boheme: {
    bg: "#F5EDE2",
    surface: "#FAF3E8",
    surface2: "#EFE2D0",
    text: "#3E2A1E",
    soft: "#8C6B57",
    accent: "#A85D3F",
    accentSoft: "#C97B5E",
    border: "#E0CFB8",
    headingFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "/images/boho_wedding_hero.png",
    accentImage: "https://images.unsplash.com/photo-1509927083803-4bd519298ac4?auto=format&fit=crop&w=1200&q=80",
  },
  modern: {
    bg: "#FFFFFF",
    surface: "#F7F7F7",
    surface2: "#EFEFEF",
    text: "#0F0F0F",
    soft: "#5C5C5C",
    accent: "#E84545",
    accentSoft: "#FF6B6B",
    border: "#E0E0E0",
    headingFont: "'Outfit', sans-serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "/images/modern_wedding_hero.png",
    accentImage: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80",
  },
  soria_fluid: {
    bg: "#fdfbf7",
    surface: "#FFFFFF",
    surface2: "#F5F0E6",
    text: "#2e2e2e",
    soft: "#666666",
    accent: "#c58361",
    accentSoft: "#e3a98c",
    border: "#e6e0d4",
    headingFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?auto=format&fit=crop&w=1200&q=80",
    accentImage: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?auto=format&fit=crop&w=1200&q=80",
  },
  ombre_velours: {
    bg: "#111111",
    surface: "#1A1A1A",
    surface2: "#2A2A2A",
    text: "#ffffff",
    soft: "#A0A0A0",
    accent: "#8c2a38",
    accentSoft: "#b24d5d",
    border: "#333333",
    headingFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80",
    accentImage: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80",
  },
  lumiere_moderne: {
    bg: "#ffffff",
    surface: "#F9F9F9",
    surface2: "#F0F0F0",
    text: "#121212",
    soft: "#777777",
    accent: "#aaaaaa",
    accentSoft: "#cccccc",
    border: "#EAEAEA",
    headingFont: "'Outfit', sans-serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "https://images.unsplash.com/photo-1532712938310-34cb3982ef74?auto=format&fit=crop&w=1200&q=80",
    accentImage: "https://images.unsplash.com/photo-1532712938310-34cb3982ef74?auto=format&fit=crop&w=1200&q=80",
  },
  epure_contemporaine: {
    bg: "#f9fafb",
    surface: "#FFFFFF",
    surface2: "#F3F4F6",
    text: "#1f2937",
    soft: "#4B5563",
    accent: "#64748b",
    accentSoft: "#94a3b8",
    border: "#E5E7EB",
    headingFont: "'Outfit', sans-serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1200&q=80",
    accentImage: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1200&q=80",
  },
  nature_romantique: {
    bg: "#f5f7f5",
    surface: "#FFFFFF",
    surface2: "#EBF0EC",
    text: "#1e3a29",
    soft: "#4A5D53",
    accent: "#8ca391",
    accentSoft: "#b4c7b8",
    border: "#d1dcd4",
    headingFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif",
    defaultHero: "/images/theme-assets/asset_36b06323.png",
    accentImage: "/images/theme-assets/asset_492e322e.png",
  },

  madras: {
    bg: "#fcf9f8",
    surface: "#FFFFFF",
    surface2: "#f3ece9",
    text: "#2a1a18",
    soft: "#84514a",
    accent: "#84514a",
    accentSoft: "#d1948b",
    border: "#e8d8d6",
    headingFont: "Cormorant Garamond, serif",
    bodyFont: "Outfit, sans-serif",
    defaultHero: "/images/theme-assets/asset_1ebb0264.png",
    accentImage: "/images/theme-assets/asset_1ebb0264.png",
  },
  heritage: {
    bg: "#f4f1ea",
    surface: "#FFFFFF",
    surface2: "#FDFCF9",
    text: "#1a1a1a",
    soft: "#4F4F4F",
    accent: "#b59f77",
    accentSoft: "#d4c8af",
    border: "#E2DCD0",
    headingFont: "Playfair Display, serif",
    bodyFont: "Cormorant Garamond, serif",
    defaultHero: "/images/theme-assets/asset_58216541.png",
    accentImage: "/images/theme-assets/asset_58216541.png",
  },
  aube_poudree: {
    bg: "#fef8f8",
    surface: "#FFFFFF",
    surface2: "#FFF5F5",
    text: "#4a4242",
    soft: "#807474",
    accent: "#e8c3c3",
    accentSoft: "#f2dcdc",
    border: "#F2E8E8",
    headingFont: "Alex Brush, cursive",
    bodyFont: "Outfit, sans-serif",
    defaultHero: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80",
    accentImage: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80",
  },
  sienne_organique: {
    bg: "#fdfaf6",
    surface: "#FFFFFF",
    surface2: "#FCF3E8",
    text: "#2c2a29",
    soft: "#5E5652",
    accent: "#b06969",
    accentSoft: "#d19797",
    border: "#EDE5DE",
    headingFont: "Cormorant Garamond, serif",
    bodyFont: "Montserrat, sans-serif",
    defaultHero: "/images/theme-assets/asset_1446cb3c.png",
    accentImage: "/images/theme-assets/asset_1446cb3c.png",
  },
  bliss_editorial: {
    bg: "#faf9f8",
    surface: "#FFFFFF",
    surface2: "#F3F1ED",
    text: "#1c1c1c",
    soft: "#66635F",
    accent: "#a89f91",
    accentSoft: "#c7beb2",
    border: "#E8E4DF",
    headingFont: "Playfair Display, serif",
    bodyFont: "Manrope, sans-serif",
    defaultHero: "/images/theme-assets/asset_336dfe65.png",
    accentImage: "/images/theme-assets/asset_336dfe65.png",
  },
  eclat_minimaliste: {
    bg: "#ffffff",
    surface: "#FAFAFA",
    surface2: "#F5F5F5",
    text: "#000000",
    soft: "#4A4A4A",
    accent: "#000000",
    accentSoft: "#e0e0e0",
    border: "#E0E0E0",
    headingFont: "Outfit, sans-serif",
    bodyFont: "Inter, sans-serif",
    defaultHero: "/images/theme-assets/asset_225a0cca.png",
    accentImage: "/images/theme-assets/asset_225a0cca.png",
  },
};

function FadeIn({ children, delay = 0, y = 30, x = 0, scale = 1, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y, x, scale }}
      animate={inView ? { opacity: 1, y: 0, x: 0, scale: 1 } : { opacity: 0, y, x, scale }}
      transition={{ type: "spring", stiffness: 60, damping: 20, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function StaggeredText({ text, theme, className, delay = 0 }) {
  const letters = Array.from(text);
  const container = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: delay * i },
    }),
  };
  const child = {
    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { type: "spring", damping: 12, stiffness: 100 } },
    hidden: { opacity: 0, y: 20, filter: "blur(10px)" },
  };

  return (
    <motion.div style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "inherit" }} variants={container} initial="hidden" animate="visible" className={className}>
      {letters.map((letter, index) => (
        <motion.span variants={child} key={index} style={{ fontFamily: theme.headingFont, whiteSpace: "pre" }}>
          {letter}
        </motion.span>
      ))}
    </motion.div>
  );
}

function Ornament({ color, size = 60 }) {
  return (
    <svg width={size} height={size / 4} viewBox="0 0 240 60" fill="none" className="mx-auto my-4">
      <line x1="0" y1="30" x2="90" y2="30" stroke={color} strokeWidth="1" />
      <line x1="150" y1="30" x2="240" y2="30" stroke={color} strokeWidth="1" />
      <circle cx="120" cy="30" r="6" fill="none" stroke={color} strokeWidth="1" />
      <circle cx="120" cy="30" r="2" fill={color} />
      <circle cx="100" cy="30" r="1.5" fill={color} />
      <circle cx="140" cy="30" r="1.5" fill={color} />
    </svg>
  );
}

function RollingNumber({ value, color, theme }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.5 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 100, damping: 10 }}
      className="text-4xl lg:text-5xl font-light" style={{ color, fontFamily: theme.headingFont }}
    >
      {String(value).padStart(2, "0")}
    </motion.div>
  );
}

function Countdown({ date, color, theme }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!date) return null;
  const diff = new Date(date).getTime() - now;
  const daysPast = Math.abs(Math.floor(diff / 86400000));
  if (diff <= 0) return <div className="text-2xl tracking-wider uppercase font-light" style={{ color, fontFamily: theme.headingFont }}>{daysPast === 0 ? "C'est aujourd'hui ✨" : "Un jour inoubliable"}</div>;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return (
    <div className="flex gap-6 lg:gap-8 justify-center" data-testid="countdown">
      {[{ v: d, l: "Jours" }, { v: h, l: "Heures" }, { v: m, l: "Min" }].map((x) => (
        <div key={x.l} className="text-center">
          <RollingNumber value={x.v} color={color} theme={theme} />
          <div className="text-[9px] uppercase tracking-[0.25em] opacity-80 mt-1">{x.l}</div>
        </div>
      ))}
    </div>
  );
}

function FAQItem({ q, a, theme }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b py-5 transition-all" style={{ borderColor: theme.border }}>
      <button onClick={() => setOpen(!open)} className="w-full flex justify-between items-center text-left py-2 focus:outline-none group">
        <span style={{ fontFamily: theme.headingFont }} className="text-lg lg:text-xl font-medium group-hover:opacity-85 transition-opacity">{q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }} className="ml-4">
          <ChevronDown size={18} style={{ color: theme.accent }} />
        </motion.span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <p className="py-3 text-sm leading-relaxed font-light" style={{ color: theme.soft }}>{a}</p>
      </motion.div>
    </div>
  );
}

function LodgingCard({ item, theme }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = item.description && item.description.length > 140;
  const desc = expanded ? item.description : (item.description || "").slice(0, 140) + (isLong ? "..." : "");

  return (
    <div className="overflow-hidden flex flex-col rounded-2xl bg-white shadow-sm hover:shadow-md transition-all border" style={{ borderColor: theme.border }}>
      {item.image ? (
        <div className="h-48 overflow-hidden">
          <img src={item.image} alt={item.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center bg-stone-50 border-b" style={{ color: theme.accent, borderColor: theme.border }}>
          <span className="text-4xl opacity-40">🏨</span>
        </div>
      )}
      <div className="p-6 flex-1 flex flex-col gap-4">
        <div>
          <h3 className="text-xl font-serif font-medium mb-1" style={{ fontFamily: theme.headingFont }}>{item.name}</h3>
          {item.capacity && (
            <div className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1.5" style={{ color: theme.accent }}>
              <span>👥 Capacité : {item.capacity}</span>
            </div>
          )}
        </div>
        <p className="text-sm font-light leading-relaxed" style={{ color: theme.soft }}>{desc}</p>
        {isLong && (
          <button onClick={() => setExpanded(!expanded)} className="text-[10px] uppercase font-semibold hover:opacity-80 transition-opacity self-start mt-2" style={{ color: theme.accent }}>
            {expanded ? "Voir moins" : "Lire la suite"}
          </button>
        )}
        {(item.booking_url || item.website || item.phone || item.email) && (
          <div className="mt-auto pt-4 border-t flex flex-wrap gap-2" style={{ borderColor: theme.border }}>
            {item.booking_url && <a href={item.booking_url} target="_blank" rel="noreferrer" className="px-4 py-2 bg-charcoal text-white rounded-lg text-[10px] uppercase font-bold tracking-widest hover:bg-black transition-colors" style={{ background: theme.accent, color: "#fff" }}>Réserver</a>}
            {item.website && <a href={item.website} target="_blank" rel="noreferrer" className="p-2 border rounded-lg hover:bg-gray-50 transition-colors" style={{ borderColor: theme.border, color: theme.accent }}><Globe size={14} /></a>}
            {item.phone && <a href={`tel:${item.phone}`} className="p-2 border rounded-lg hover:bg-gray-50 transition-colors" style={{ borderColor: theme.border, color: theme.accent }}><Phone size={14} /></a>}
            {item.email && <a href={`mailto:${item.email}`} className="p-2 border rounded-lg hover:bg-gray-50 transition-colors" style={{ borderColor: theme.border, color: theme.accent }}><Mail size={14} /></a>}
          </div>
        )}
      </div>
    </div>
  );
}

function RandomizedMessage({ msg, styleType, theme, index }) {
  const [iteration, setIteration] = useState(0);

  const rX = 5 + Math.random() * 80;
  const rY = 10 + Math.random() * 70;
  // Décalage pour la première apparition, puis délai court entre deux apparitions
  const delay = iteration === 0 ? (index * 2) % 15 : Math.random() * 2;

  const handleComplete = () => setIteration(i => i + 1);

  if (styleType === 'bubbles') {
    const duration = 12 + Math.random() * 8;
    return (
      <motion.div
        key={`bubble-${iteration}`}
        initial={{ y: "100vh", x: `${rX}vw`, opacity: 0 }}
        animate={{ y: "-20vh", opacity: [0, 1, 1, 0] }}
        transition={{ duration, delay, ease: "linear" }}
        onAnimationComplete={handleComplete}
        className="absolute p-5 rounded-3xl rounded-br-none shadow-lg text-sm font-light leading-relaxed max-w-[280px]"
        style={{ background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }}
      >
        <span className="font-bold tracking-widest uppercase block mb-1 text-[9px] opacity-80" style={{ color: theme.accent }}>{msg.guest_name}</span>
        {msg.content}
      </motion.div>
    );
  }

  if (styleType === 'constellation') {
    const duration = 6 + Math.random() * 6;
    return (
      <motion.div
        key={`star-${iteration}`}
        initial={{ opacity: 0, scale: 0.8, x: `${rX}vw`, y: `${rY}vh` }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.8] }}
        transition={{ duration, delay, ease: "easeInOut" }}
        onAnimationComplete={handleComplete}
        className="absolute p-5 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white shadow-2xl max-w-[280px]"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-200 mb-2">{msg.guest_name}</div>
        <p className="text-sm font-light">{msg.content}</p>
      </motion.div>
    );
  }

  if (styleType === 'scroll') {
    const duration = 10 + Math.random() * 4;
    return (
      <motion.div
        key={`scroll-${iteration}`}
        initial={{ opacity: 0, x: `${rX}vw`, y: `${rY}vh` }}
        animate={{ opacity: [0, 0.9, 0.9, 0] }}
        transition={{ duration, delay, ease: "easeInOut" }}
        onAnimationComplete={handleComplete}
        className="absolute p-8 font-serif italic text-xl leading-relaxed text-center max-w-sm"
        style={{ color: '#4a3f35', textShadow: '0 2px 10px rgba(255,255,255,0.8)' }}
      >
        "{msg.content}"
        <div className="mt-3 not-italic font-sans text-[9px] uppercase tracking-widest font-bold" style={{ color: theme.accent }}>— {msg.guest_name}</div>
      </motion.div>
    );
  }

  // default / masonry
  const duration = 18 + Math.random() * 10;
  const rotate = -15 + Math.random() * 30;
  return (
    <motion.div
      key={`masonry-${iteration}`}
      initial={{ y: "-30vh", x: `${rX}vw`, rotate, opacity: 0 }}
      animate={{ y: "110vh", opacity: [0, 1, 1, 0], rotate: rotate + 30 }}
      transition={{ duration, delay, ease: "linear" }}
      onAnimationComplete={handleComplete}
      className="absolute p-5 border shadow-xl bg-white max-w-[260px]"
      style={{ borderColor: theme.border }}
    >
      <div className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: theme.accent }}>{msg.guest_name}</div>
      <p className="text-sm font-light text-gray-700">{msg.content}</p>
    </motion.div>
  );
}

function FloatingMessagesOverlay({ messages, styleType, theme }) {
  if (!messages || messages.length === 0) return null;

  return (
    <div className={`fixed inset-0 pointer-events-none z-30 overflow-hidden ${styleType === 'constellation' ? 'bg-slate-900/5 mix-blend-multiply' : ''}`}>
      {messages.map((msg, i) => (
        <RandomizedMessage key={msg.id} msg={msg} styleType={styleType} theme={theme} index={i} />
      ))}
    </div>
  );
}

export default function PublicWedding() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const rsvpToken = searchParams.get("token");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [publicSessionUnlocked, setPublicSessionUnlocked] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [isSticky, setIsSticky] = useState(false);
  const [siteEntered, setSiteEntered] = useState(false);
  const [splashFailed, setSplashFailed] = useState(false);
  const [isPortraitMobile, setIsPortraitMobile] = useState(false);
  // This hook must be declared before every possible early return below.  The
  // public page first renders while its data is loading, then renders again
  // with the wedding; declaring it later changes React's hook order and
  // crashes the page before the splash can be displayed.
  const splashVideoRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  const [guestMessages, setGuestMessages] = useState([]);
  const [newGuestName, setNewGuestName] = useState("");
  const [newGuestMsg, setNewGuestMsg] = useState("");
  const [isSubmittingMsg, setIsSubmittingMsg] = useState(false);

  const splashDesktopVideoUrl = data?.wedding?.splash_video_url || "";
  const splashMobileVideoUrl = data?.wedding?.splash_mobile_video_url || "";
  const splashVideoUrl = isPortraitMobile
    ? (splashMobileVideoUrl || splashDesktopVideoUrl)
    : (splashDesktopVideoUrl || splashMobileVideoUrl);
  const showSplash = Boolean(data?.wedding?.enable_splash_screen && splashVideoUrl);
  const shouldDisplaySplash = showSplash && !splashFailed;

  useEffect(() => {
    if (shouldDisplaySplash && !siteEntered && splashVideoRef.current) {
      splashVideoRef.current.defaultMuted = true;
      splashVideoRef.current.muted = true;
      const playback = splashVideoRef.current.play();
      // Some older embedded browsers do not return the Promise mandated by
      // modern HTMLMediaElement.play().  Treat autoplay as progressive
      // enhancement instead of letting that browser break the whole page.
      if (playback && typeof playback.catch === "function") {
        playback.catch((e) => console.warn("Video autoplay blocked:", e));
      }
    }
  }, [shouldDisplaySplash, siteEntered, splashVideoUrl]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const syncViewport = () => setIsPortraitMobile(media.matches);
    syncViewport();
    media.addEventListener?.("change", syncViewport);
    return () => media.removeEventListener?.("change", syncViewport);
  }, []);

  const loadData = useCallback(() => {
    const t = Date.now();
    api.get(`/public/wedding/${slug}?t=${t}`).then((r) => {
      const responseData = r.data;
      const w = responseData?.wedding;
      const processUrl = (url) => {
        if (!url) return url;
        if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
        // Legacy uploads were served by the retired local server on port 8000.
        // Those files are no longer available there, so let each theme use its
        // local fallback until the couple imports a replacement in the editor.
        if (/^https?:\/\/(localhost|127\.0\.0\.1):8000\/static\/uploads\//.test(url)) return "";
        return url;
      };
      if (w) {
        w.hero_image = processUrl(w.hero_image);
        w.story_image = processUrl(w.story_image);
        w.venue_image = processUrl(w.venue_image);
        w.dress_code_image = processUrl(w.dress_code_image);
        // Uploaded splash videos are exposed through the backend's protected
        // public asset route, just like the other editable media fields.
        w.splash_video_url = processUrl(w.splash_video_url);
        w.splash_mobile_video_url = processUrl(w.splash_mobile_video_url);
        if (Array.isArray(w.nature_gallery_images)) {
          w.nature_gallery_images = w.nature_gallery_images.map(processUrl);
        }
        if (Array.isArray(w.photos)) {
          w.photos = w.photos.map(p => ({ ...p, url: processUrl(p.url) }));
        }
      }
      setData(responseData);
      setError(null);
      setNeedsPassword(false);
    }).catch((requestError) => {
      if (requestError.response?.status === 401) {
        setNeedsPassword(true);
        return;
      }
      setError(requestError.response?.status === 403 ? "Accès refusé" : "Site introuvable");
    });
    api.get(`/public/wedding/${slug}/gifts?t=${t}`).then((r) => setGifts(r.data)).catch(() => {});
    api.get(`/public/wedding/${slug}/messages?t=${t}`).then((r) => setGuestMessages(r.data)).catch(() => {});

    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      alert("✨ Merci infiniment pour votre généreuse contribution ! 💛");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const unlock = async (event) => {
    event.preventDefault();
    if (!password || unlocking) return;
    setUnlocking(true);
    setPasswordError("");
    try {
      await api.post(`/public/wedding/${slug}/unlock`, {}, { headers: { "X-Wedding-Password": password } });
      setPassword("");
      setPublicSessionUnlocked(true);
      loadData();
    } catch (requestError) {
      setPasswordError(requestError.response?.status === 401 ? "Mot de passe incorrect" : "Impossible de déverrouiller le site.");
    } finally {
      setUnlocking(false);
    }
  };

  const logoutPublicSession = async () => {
    try {
      await api.post(`/public/wedding/${slug}/logout`);
    } finally {
      setPublicSessionUnlocked(false);
      setData(null);
      setNeedsPassword(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight - 80) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getContrastColor = (hexcolor) => {
    if (!hexcolor) return "#FFFFFF";
    const hex = hexcolor.replace("#", "");
    if (hex.length !== 6) return "#FFFFFF";
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 135 ? "#1b1c1c" : "#FFFFFF";
  };

  const claimGift = async (gift) => {
    const guestName = prompt("Votre nom ?", "");
    if (!guestName) return;
    try {
      if (gift.type === "cash") {
        const amt = parseFloat(prompt("Montant (€) ?", "50") || "0");
        if (!amt || isNaN(amt)) return;

        const response = await api.post("/stripe/checkout", {
          gift_id: gift.id,
          amount: amt,
          guest_name: guestName,
          rsvp_token: ""
        });

        if (response.data && response.data.url) {
          window.location.href = response.data.url;
          return;
        }
      } else {
        await api.post(`/public/wedding/${slug}/gifts/${gift.id}/claim`, { guest_name: guestName });
      }
      const r = await api.get(`/public/wedding/${slug}/gifts`);
      setGifts(r.data);
      toast.success("Merci de tout cœur 💛");
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const handleSendGuestMessage = async (e) => {
    e.preventDefault();
    if (!newGuestName.trim() || !newGuestMsg.trim()) return;
    setIsSubmittingMsg(true);
    try {
      const response = await api.post(`/public/wedding/${slug}/messages`, {
        guest_name: newGuestName,
        content: newGuestMsg,
      });
      setGuestMessages((prev) => [response.data, ...prev]);
      setNewGuestName("");
      setNewGuestMsg("");
      toast.success("✨ Votre message a été publié avec succès ! 💛");
    } catch (e) {
      toast.error("Erreur lors de l'envoi du message : " + (e.response?.data?.detail || e.message));
    } finally {
      setIsSubmittingMsg(false);
    }
  };

  if (needsPassword) return <div className="min-h-screen flex items-center justify-center bg-ivory p-6"><form onSubmit={unlock} className="bg-white p-8 rounded-sm shadow-sm w-full max-w-sm space-y-4" data-testid="cloudpage-unlock-form"><h1 className="font-serif text-2xl">Accès privé</h1><p className="text-sm text-charcoal-soft">Saisissez le mot de passe communiqué par les mariés.</p><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" aria-label="Mot de passe du site" className="w-full border p-3" />{passwordError && <p role="alert" className="text-sm text-red-600">{passwordError}</p>}<button type="submit" disabled={!password || unlocking} className="btn-sage w-full py-3">{unlocking ? "Déverrouillage…" : "Déverrouiller"}</button></form></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="font-serif text-2xl">{error}</div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="font-serif text-2xl">Chargement…</div></div>;

  const { wedding, invitation } = data;
  const params = new URLSearchParams(window.location.search);
  const previewTheme = params.get("previewTheme");
  let activeThemeId = previewTheme || wedding.theme;
  if (activeThemeId === "elegant-gold" || (!THEMES[activeThemeId] && !["nature_romantique", "madras", "chic", "boheme", "epure_contemporaine"].includes(activeThemeId))) {
    activeThemeId = "nature_romantique";
  }
  const isPreviewingOtherTheme = previewTheme && previewTheme !== wedding.theme;

  const customTheme = (wedding.custom_themes || []).find((ct) => ct.id === activeThemeId);
  const t = customTheme || THEMES[activeThemeId] || THEMES.chic;

  // If previewing a different theme, don't use the saved hero_image and theme_color of the current theme
  const heroImage = isPreviewingOtherTheme ? t.defaultHero : (wedding.hero_image || t.defaultHero);
  const fmtDate = wedding.date ? new Date(wedding.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";
  const sidePanelBg = isPreviewingOtherTheme ? t.accent : (wedding.theme_color || t.accent);
  const sidePanelText = getContrastColor(sidePanelBg);

  const accentImage = isPreviewingOtherTheme ? t.accentImage : (wedding.story_image || t.accentImage);

  const DEFAULT_SECTIONS_ORDER = ["story", "invitation", "programme", "gallery", "infos", "dress_code", "lodgings", "faq", "guestbook"];
  const finalSectionsOrder = wedding.sections_order ? [...wedding.sections_order] : [...DEFAULT_SECTIONS_ORDER];

  // Append any new sections that aren't in the saved sections_order
  const missingSections = DEFAULT_SECTIONS_ORDER.filter(id => !finalSectionsOrder.includes(id));
  if (missingSections.length > 0) finalSectionsOrder.push(...missingSections);

  const isSectionVisible = (sectionId) => {
    if (sectionId === "gifts" && wedding.show_gifts === false) return false;
    if (!wedding.active_sections) return true;
    // If the section is a newly added feature (like dress_code) and the user hasn't saved their site since,
    // it won't be in sections_order either. In this case, default it to visible.
    if (!wedding.sections_order?.includes(sectionId)) return true;

    const isActive = wedding.active_sections.includes(sectionId);
    if (!isActive) return false;

    // Check time-gated visibility
    if (wedding.section_unlock_dates && wedding.section_unlock_dates[sectionId]) {
      const unlockDate = new Date(wedding.section_unlock_dates[sectionId]);
      if (new Date() < unlockDate) {
        return false; // Still locked
      }
    }

    return true;
  };

  const isSocialVisible = wedding.show_social !== false && (
    !wedding.social_unlock_date || new Date() >= new Date(`${wedding.social_unlock_date}T00:00:00`)
  );
  // Themes share the same wedding object.  Give them an already-hidden social
  // module before its publication date so each theme follows the same rule.
  const weddingForTheme = isSocialVisible ? wedding : { ...wedding, show_social: false };

  if (shouldDisplaySplash && !siteEntered) {
    return (
      <div className="fixed inset-0 z-[1000] bg-black flex items-center justify-center overflow-hidden">
        <video
          ref={splashVideoRef}
          src={splashVideoUrl}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => setSiteEntered(true)}
          onError={() => {
            // A malformed, deleted or unsupported video must never lock a
            // public wedding page on a black splash screen.
            console.warn("Splash video unavailable; opening the wedding site.");
            setSplashFailed(true);
          }}
          className={`absolute inset-0 w-full h-full ${isPortraitMobile && !splashMobileVideoUrl ? "object-contain" : "object-cover"}`}
        />
        <div className="absolute inset-0 bg-black/30 z-10" />
        <button
          onClick={() => setSiteEntered(true)}
          className="absolute bottom-12 z-20 px-8 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full font-light tracking-widest uppercase text-sm transition-all"
        >
          Passer l'introduction
        </button>
      </div>
    );
  }

  if (activeThemeId === "nature_romantique") {
    return <NatureRomantique wedding={weddingForTheme} heroImage={heroImage} accentImage={accentImage} isSectionVisible={isSectionVisible} />;
  }

  if (activeThemeId === "madras") {
    return <MadrasEclatant wedding={weddingForTheme} heroImage={heroImage} accentImage={accentImage} isSectionVisible={isSectionVisible} />;
  }

  if (activeThemeId === "chic") {
    return <HeritageChic wedding={weddingForTheme} heroImage={heroImage} accentImage={accentImage} isSectionVisible={isSectionVisible} />;
  }

  if (activeThemeId === "boheme") {
    return <BohemeAubePoudree wedding={weddingForTheme} heroImage={heroImage} accentImage={accentImage} isSectionVisible={isSectionVisible} />;
  }

  if (activeThemeId === "epure_contemporaine") {
    return <EpureContemporaine wedding={weddingForTheme} heroImage={heroImage} accentImage={accentImage} showSection={isSectionVisible} />;
  }

  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.bodyFont }} className="overflow-x-hidden relative" data-testid="public-wedding">

      <FloatingMessagesOverlay messages={guestMessages} styleType={wedding.guestbook_style || 'masonry'} theme={t} />

      {!isPreviewingOtherTheme && wedding.bg_image && (
        <div
          className="fixed inset-0 pointer-events-none z-0 transition-all duration-300"
          style={{
            backgroundImage: `url(${wedding.bg_image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            opacity: wedding.bg_opacity ?? 1.0,
            filter: `blur(${wedding.bg_blur ?? 0}px)`,
            transform: "scale(1.05)",
          }}
        />
      )}

      {/* STICKY NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isSticky ? "translate-y-0 opacity-100 shadow-sm" : "-translate-y-full opacity-0 pointer-events-none"}`} style={{ background: t.surface, borderBottom: `1px solid ${t.border}` }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="font-serif text-lg font-bold" style={{ fontFamily: t.headingFont }}>
            {wedding.bride_name} & {wedding.groom_name}
          </div>
          <div className="flex gap-6 text-xs uppercase tracking-widest font-semibold overflow-x-auto scrollbar-none py-1">
            <a href="#hero" className="hover:opacity-75 transition-opacity whitespace-nowrap">Accueil</a>
            {finalSectionsOrder.map(id => {
              if (!isSectionVisible(id)) return null;
              if (id === "story" && wedding.story) return <a key={id} href="#story" className="hover:opacity-75 transition-opacity whitespace-nowrap">Récit</a>;
              if (id === "invitation" && wedding.show_invitation) return <a key={id} href="#faire-part" className="hover:opacity-75 transition-opacity whitespace-nowrap">Faire-part</a>;
              if (id === "programme" && wedding.programme && wedding.programme.length > 0) return <a key={id} href="#programme" className="hover:opacity-75 transition-opacity whitespace-nowrap">Programme</a>;
              if (id === "gallery") return <a key={id} href="#gallery" className="hover:opacity-75 transition-opacity whitespace-nowrap">Galerie</a>;
              if (id === "infos" && (wedding.access_info || wedding.venue_address)) return <a key={id} href="#infos" className="hover:opacity-75 transition-opacity whitespace-nowrap">Lieu</a>;
              if (id === "dress_code" && (wedding.dress_code || (wedding.dress_code_colors && wedding.dress_code_colors.length > 0))) return <a key={id} href="#dress_code" className="hover:opacity-75 transition-opacity whitespace-nowrap">Dress Code</a>;
              if (id === "lodgings" && ((wedding.lodgings && wedding.lodgings.length > 0) || wedding.accommodations)) return <a key={id} href="#lodgings" className="hover:opacity-75 transition-opacity whitespace-nowrap">Hébergements</a>;
              if (id === "faq" && wedding.faqs && wedding.faqs.length > 0) return <a key={id} href="#faq" className="hover:opacity-75 transition-opacity whitespace-nowrap">FAQ</a>;
              if (id === "guestbook") return <a key={id} href="#guestbook" className="hover:opacity-75 transition-opacity whitespace-nowrap">Livre d'or</a>;
              return null;
            })}
            {isSectionVisible("gifts") && gifts.length > 0 && <a href="#gifts" className="hover:opacity-75 transition-opacity whitespace-nowrap">Cadeaux</a>}
          </div>
        </div>
      </nav>

      {/* HERO SPLIT-SCREEN */}
      <section id="hero" className="relative min-h-screen flex flex-col md:flex-row overflow-hidden" data-testid="section-hero">

        {/* Left Side: Photo Cover */}
        <div className="w-full md:w-3/5 h-[50vh] md:h-screen relative overflow-hidden">
          <motion.div
            initial={{ scale: 1.15 }}
            animate={{ scale: 1.05 }}
            transition={{ duration: 10, ease: "easeOut" }}
            style={{ y: heroY, opacity: heroOpacity, backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
            className="absolute inset-0"
          />
          <div className="absolute inset-0 bg-black/10 md:bg-black/5" />
          <div className="absolute top-6 left-6 z-20 text-white text-xs uppercase tracking-[0.3em] font-light">
            Save the Date
          </div>
        </div>

        {/* Right Side: Event Details Card */}
        <div className="w-full md:w-2/5 md:h-screen flex flex-col justify-between p-8 md:p-12 lg:p-16 relative" style={{ backgroundColor: sidePanelBg, color: sidePanelText }}>
          <div className="hidden md:flex justify-end text-xs uppercase tracking-[0.3em] opacity-80">
            {wedding.hashtag ? `#${wedding.hashtag.replace(/^#/, '')}` : "Ceremind"}
          </div>

          <div className="my-auto py-12 md:py-0 text-center md:text-left">
            <div className="text-[10px] uppercase tracking-[0.4em] opacity-80 mb-6">{fmtDate || "Bientôt"}</div>

            <h1 style={{ fontFamily: t.headingFont }} className="font-light leading-[1.05] tracking-tight flex flex-col items-center md:items-start">
              <StaggeredText text={wedding.bride_name || "Mariée"} theme={t} className="text-5xl lg:text-7xl" delay={0.2} />
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 0.8 }} transition={{ delay: 0.6, type: "spring", stiffness: 100 }} className="my-3 text-2xl lg:text-3xl italic">&</motion.div>
              <StaggeredText text={wedding.groom_name || "Marié"} theme={t} className="text-5xl lg:text-7xl" delay={0.8} />
            </h1>

            {wedding.venue && (
              <div className="mt-8 pt-8 border-t flex flex-col gap-1.5 items-center md:items-start" style={{ borderColor: `${sidePanelText}30` }}>
                <span className="text-xs uppercase tracking-[0.3em] font-medium opacity-90">{wedding.venue}</span>
                {wedding.venue_address && <span className="text-xs opacity-75 font-light leading-relaxed max-w-sm">{wedding.venue_address}</span>}
              </div>
            )}

            <div className="mt-10 flex justify-center md:justify-start">
              <motion.a
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                href={rsvpToken ? `/rsvp/${rsvpToken}` : "#closing"}
                className="inline-flex items-center gap-2 px-8 py-3.5 border rounded-full text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors duration-300 font-medium"
                style={{ borderColor: sidePanelText, color: sidePanelText }}
              >
                Répondre au RSVP
              </motion.a>
            </div>
          </div>

          <div className="flex flex-col gap-4 text-center md:text-left mt-auto">
            <Countdown date={wedding.date} color={sidePanelText} theme={t} />
            <div className="text-[9px] uppercase tracking-[0.3em] opacity-60">↓ Faire défiler</div>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      {finalSectionsOrder.map(sectionId => {
        if (!isSectionVisible(sectionId)) return null;

        if (sectionId === "story" && wedding.story) {
          return (
            <section key="story" id="story" className="px-6 lg:px-16 py-32 lg:py-48 max-w-7xl mx-auto" data-testid="section-story">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <FadeIn x={-50} className="lg:col-span-5 lg:col-start-1">
                  <div className="aspect-[3/4] overflow-hidden rounded-2xl shadow-sm">
                    <motion.img whileHover={{ scale: 1.05 }} transition={{ duration: 0.5 }} src={wedding.story_image || t.accentImage} alt="" className="w-full h-full object-cover" />
                  </div>
                </FadeIn>
                <FadeIn x={50} delay={0.2} className="lg:col-span-6 lg:col-start-7 text-center lg:text-left">
                  <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Chapitre I</div>
                  <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light leading-[1.05] mb-6">
                    Notre <span className="italic" style={{ color: t.accent }}>histoire</span>
                  </h2>
                  <Ornament color={t.accent} />
                  <p className="mt-8 text-base lg:text-lg leading-[1.9] whitespace-pre-wrap font-light text-justify" style={{ color: t.soft }}>{wedding.story}</p>
                </FadeIn>
              </div>
            </section>
          );
        }

        if (sectionId === "invitation" && wedding.show_invitation && invitation) {
          return (
            <section key="invitation" id="faire-part" className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface }} data-testid="section-faire-part">
              <div className="max-w-4xl mx-auto">
                <FadeIn className="text-center mb-16">
                  <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Faire-part</div>
                  <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                    Le <span className="italic" style={{ color: t.accent }}>faire-part</span>
                  </h2>
                  <div className="mt-6"><Ornament color={t.accent} /></div>
                </FadeIn>

                <FadeIn delay={0.1} className="flex flex-col md:flex-row justify-center items-center gap-12">
                  {(wedding.invitation_format === "static" || wedding.invitation_format === "both") && (
                    <div className="bg-white p-4 rounded-3xl shadow-xl border max-w-sm w-full mx-auto relative overflow-hidden" style={{ borderColor: t.border }}>
                      <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-multiply z-20" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}></div>
                      <img src={invitation.bg_id === 'floral' ? "https://static.prod-images.emergentagent.com/jobs/c7c691e5-c2e1-48ee-9432-1c4ee8093bb8/images/df8745e076e983867ec8755bdfd743907b0721ed8db2092e586f95cc52d70ea6.png" : "/images/theme-assets/asset_9cd317cd.png"} alt="Faire-part" className="w-full aspect-[3/4] object-cover rounded-2xl" />
                    </div>
                  )}
                </FadeIn>
              </div>
            </section>
          );
        }

        if (sectionId === "programme" && wedding.programme && wedding.programme.length > 0) {
          return (
            <section key="programme" id="programme" className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface2 }} data-testid="section-programme">
              <div className="max-w-4xl mx-auto">
                <FadeIn className="text-center mb-20">
                  <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Le grand jour</div>
                  <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                    Le <span className="italic" style={{ color: t.accent }}>programme</span>
                  </h2>
                  <div className="mt-6"><Ornament color={t.accent} /></div>
                </FadeIn>

                <div className="relative">
                  <div className="absolute left-[80px] lg:left-[120px] top-0 bottom-0 w-px hidden md:block" style={{ background: t.border }} />
                  <div className="space-y-12">
                    {wedding.programme.map((p, i) => (
                      <FadeIn key={i} delay={i * 0.08}>
                        <div className="grid grid-cols-1 md:grid-cols-[80px_1fr] lg:grid-cols-[120px_1fr] gap-4 lg:gap-12 items-baseline">
                          <div className="relative">
                            <div style={{ fontFamily: t.headingFont, color: t.accent }} className="text-2xl lg:text-3xl font-light">{p.time || "—"}</div>
                            <div className="absolute right-0 top-3.5 w-2.5 h-2.5 rounded-full hidden md:block translate-x-[5px]" style={{ background: t.accent }} />
                          </div>
                          <div className="bg-white/50 md:bg-transparent p-6 md:p-0 rounded-2xl border md:border-none" style={{ borderColor: t.border }}>
                            <div style={{ fontFamily: t.headingFont }} className="text-2xl lg:text-3xl font-normal mb-2">{p.title}</div>
                            {p.description && <div className="text-sm leading-relaxed font-light mb-3" style={{ color: t.soft }}>{p.description}</div>}
                          </div>
                        </div>
                      </FadeIn>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        }

        if (sectionId === "gallery") {
          return (
            <section key="gallery" id="gallery" className="px-6 lg:px-16 py-32 lg:py-48" data-testid="section-photos">
              <FadeIn className="text-center mb-20">
                <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Souvenirs</div>
                <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                  Quelques <span className="italic" style={{ color: t.accent }}>instants</span>
                </h2>
              </FadeIn>
              {(wedding.photos || []).filter((photo) => photo?.url && photo.status !== "pending" && photo.status !== "rejected").length > 0 ? (
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-12 gap-4">
                  {wedding.photos.filter((photo) => photo?.url && photo.status !== "pending" && photo.status !== "rejected").map((p, i) => (
                    <FadeIn key={i} delay={i * 0.05} className="overflow-hidden rounded-2xl shadow-sm group">
                      <img src={p.url} alt="" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    </FadeIn>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-sm font-light border border-dashed rounded-sm max-w-lg mx-auto" style={{ borderColor: t.border, color: t.soft }}>
                  <span className="text-3xl mb-3 block">📸</span>
                  Aucune photo partagée pour le moment.
                </div>
              )}
            </section>
          );
        }

        if (sectionId === "infos" && (wedding.access_info || wedding.venue_address)) {
          return (
            <section key="infos" id="infos" className="relative" data-testid="section-infos">
              <div className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface }}>
                <div className="max-w-5xl mx-auto">
                  <FadeIn className="text-center mb-20">
                    <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>En pratique</div>
                    <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                      Le <span className="italic" style={{ color: t.accent }}>lieu</span>
                    </h2>
                  </FadeIn>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                    {wedding.venue_address && (
                      <FadeIn>
                        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: t.accent }}>Adresse</div>
                        <div className="text-2xl mb-2 font-medium" style={{ fontFamily: t.headingFont }}>{wedding.venue}</div>
                        <div className="text-sm whitespace-pre-wrap leading-relaxed font-light" style={{ color: t.soft }}>{wedding.venue_address}</div>
                      </FadeIn>
                    )}
                    <div className="space-y-12">
                      {wedding.access_info && (
                        <FadeIn delay={0.1}>
                          <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: t.accent }}>Accès</div>
                          <div className="text-sm whitespace-pre-wrap leading-relaxed font-light" style={{ color: t.soft }}>{wedding.access_info}</div>
                        </FadeIn>
                      )}
                    </div>
                  </div>

                  {(wedding.venue_address || wedding.venue) && (
                    <FadeIn data-testid="section-map">
                      <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl border" style={{ borderColor: t.border }}>
                        <iframe
                          title="map"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent((wedding.venue || "") + " " + (wedding.venue_address || ""))}&output=embed`}
                        />
                      </div>
                      <div className="flex flex-wrap gap-3 mt-6 justify-center">
                        <a target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((wedding.venue || "") + " " + (wedding.venue_address || ""))}`} className="px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold border transition-all rounded-sm hover:opacity-80" style={{ borderColor: t.border, color: t.text, background: t.surface }} data-testid="open-google-maps">Google Maps</a>
                        <a target="_blank" rel="noreferrer" href={`https://waze.com/ul?q=${encodeURIComponent((wedding.venue || "") + " " + (wedding.venue_address || ""))}`} className="px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold border transition-all rounded-sm hover:opacity-80" style={{ borderColor: t.border, color: t.text, background: t.surface }} data-testid="open-waze">Waze</a>
                        <a target="_blank" rel="noreferrer" href={`http://maps.apple.com/?q=${encodeURIComponent((wedding.venue || "") + " " + (wedding.venue_address || ""))}`} className="px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold border transition-all rounded-sm hover:opacity-80" style={{ borderColor: t.border, color: t.text, background: t.surface }} data-testid="open-apple-maps">Apple Plans</a>
                      </div>
                    </FadeIn>
                  )}
                </div>
              </div>
            </section>
          );
        }

        if (sectionId === "dress_code" && (wedding.dress_code || (wedding.dress_code_colors && wedding.dress_code_colors.length > 0))) {
          return (
            <section key="dress_code" id="dress_code" className="relative" data-testid="section-dress_code">
              <div className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface }}>
                <div className="max-w-5xl mx-auto">
                  <FadeIn className="text-center mb-16">
                    <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Code Vestimentaire</div>
                    <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                      <span className="italic" style={{ color: t.accent }}>Dress Code</span>
                    </h2>
                  </FadeIn>

                  <FadeIn className="max-w-2xl mx-auto text-center space-y-12">
                    {wedding.dress_code && (
                      <div className="text-lg whitespace-pre-wrap leading-relaxed font-light" style={{ color: t.text }}>
                        {wedding.dress_code}
                      </div>
                    )}

                    {wedding.dress_code_colors && wedding.dress_code_colors.length > 0 && (
                      <div className="flex flex-col items-center">
                        <div className="text-[10px] uppercase tracking-[0.3em] mb-6" style={{ color: t.accent }}>Charte de Couleurs</div>
                        <div className="flex flex-wrap justify-center gap-6">
                          {wedding.dress_code_colors.map((color, index) => (
                            <div key={index} className="flex flex-col items-center gap-3">
                              <div
                                className="w-16 h-16 rounded-full shadow-md"
                                style={{ backgroundColor: color }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </FadeIn>
                </div>
              </div>
            </section>
          );
        }

        if (sectionId === "lodgings" && ((wedding.lodgings && wedding.lodgings.length > 0) || wedding.accommodations)) {
          return (
            <section key="lodgings" id="lodgings" className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.bg }} data-testid="section-lodgings">
              <div className="max-w-6xl mx-auto">
                <FadeIn className="text-center mb-20">
                  <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Séjourner</div>
                  <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                    Où <span className="italic" style={{ color: t.accent }}>dormir</span>
                  </h2>
                </FadeIn>
                {wedding.lodgings && wedding.lodgings.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {wedding.lodgings.map((lodging, i) => (
                      <FadeIn key={i} delay={i * 0.08}><LodgingCard item={lodging} theme={t} /></FadeIn>
                    ))}
                  </div>
                ) : (
                  <FadeIn className="text-center text-sm font-light">{wedding.accommodations}</FadeIn>
                )}
              </div>
            </section>
          );
        }

        if (sectionId === "faq" && wedding.faqs && wedding.faqs.length > 0) {
          return (
            <section key="faq" id="faq" className="px-6 lg:px-16 py-32 lg:py-48 max-w-4xl mx-auto" data-testid="section-faq">
              <FadeIn className="text-center mb-16">
                <h2 style={{ fontFamily: t.headingFont }} className="text-5xl font-light">Questions <span className="italic" style={{ color: t.accent }}>Fréquentes</span></h2>
              </FadeIn>
              {wedding.faqs.map((faq, i) => <FAQItem key={i} q={faq.question} a={faq.answer} theme={t} />)}
            </section>
          );
        }

        if (sectionId === "guestbook") {
          return (
            <section key="guestbook" id="guestbook" className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface2 }} data-testid="section-guestbook">
              <div className="max-w-2xl mx-auto">
                <div className="bg-white border p-8 lg:p-12 rounded-sm space-y-8 shadow-2xl relative z-20" style={{ borderColor: t.border, background: t.surface }}>
                  <div className="text-center">
                    <h3 style={{ fontFamily: t.headingFont }} className="text-3xl font-light text-charcoal mb-2">Laissez une trace de votre passage</h3>
                    <p className="text-sm font-light opacity-80" style={{ color: t.soft }}>Votre message rejoindra immédiatement ceux des autres invités et flottera sur le site.</p>
                  </div>

                  <form onSubmit={handleSendGuestMessage} className="space-y-6">
                    <div>
                      <span className="overline text-[10px] tracking-widest uppercase font-semibold block mb-2" style={{ color: t.soft }}>Votre Nom ou Prénom</span>
                      <input
                        type="text"
                        required
                        placeholder="Ex: Sophie & Thomas"
                        value={newGuestName}
                        onChange={(e) => setNewGuestName(e.target.value)}
                        className="w-full px-5 py-4 border text-sm font-light focus:outline-none focus:border-sage transition-all rounded-sm bg-transparent"
                        style={{ borderColor: t.border, color: t.text }}
                        data-testid="guestbook-name-input"
                      />
                    </div>
                    <div>
                      <span className="overline text-[10px] tracking-widest uppercase font-semibold block mb-2" style={{ color: t.soft }}>Votre Mot Doux</span>
                      <textarea
                        required
                        rows="4"
                        placeholder="Tous nos vœux de bonheur pour cette merveilleuse aventure..."
                        value={newGuestMsg}
                        onChange={(e) => setNewGuestMsg(e.target.value)}
                        className="w-full px-5 py-4 border text-sm font-light focus:outline-none focus:border-sage transition-all resize-none rounded-sm bg-transparent"
                        style={{ borderColor: t.border, color: t.text }}
                        data-testid="guestbook-message-input"
                      />
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmittingMsg}
                      className="w-full py-4 text-xs font-bold uppercase tracking-widest transition-all rounded-sm hover:opacity-90 disabled:opacity-50 mt-4 shadow-lg"
                      style={{ background: t.accent || '#84514a', color: '#FFF' }}
                      data-testid="guestbook-submit-button"
                    >
                      {isSubmittingMsg ? "Envol du message..." : "Envoyer mon mot doux ✨"}
                    </motion.button>
                  </form>
                </div>
              </div>
            </section>
          );
        }

        return null;
      })}

      {/* GIFTS */}
      {isSectionVisible("gifts") && gifts.length > 0 && (
        <section id="gifts" className="px-6 lg:px-16 py-32 lg:py-48" style={{ background: t.surface2 }} data-testid="section-gifts">
          <div className="max-w-6xl mx-auto">
            <FadeIn className="text-center mb-20">
              <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: t.accent }}>Avec tout notre cœur</div>
              <h2 style={{ fontFamily: t.headingFont }} className="text-5xl lg:text-6xl font-light">
                Liste de <span className="italic" style={{ color: t.accent }}>cadeaux</span>
              </h2>
              <p className="text-sm mt-6 max-w-md mx-auto leading-relaxed font-light" style={{ color: t.soft }}>
                Votre présence est le plus beau des cadeaux. Si toutefois vous souhaitez nous gâter, voici quelques idées qui nous feraient plaisir.
              </p>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gifts.map((g, i) => (
                <FadeIn key={g.id} delay={i * 0.06}>
                  <div className="overflow-hidden h-full flex flex-col rounded-2xl shadow-sm border bg-white" style={{ borderColor: t.border }} data-testid={`public-gift-${g.id}`}>
                    {g.image ? (
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={g.image} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] flex items-center justify-center bg-stone-50 border-b" style={{ color: t.accent, borderColor: t.border }}>
                        <div className="text-4xl font-serif font-light">{g.type === "cash" ? "€" : "🎁"}</div>
                      </div>
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: t.accent }}>{g.type === "cash" ? "Cagnotte" : "Cadeau"}</div>
                      <div className="text-2xl mb-2 font-medium" style={{ fontFamily: t.headingFont }}>{g.name}</div>
                      {g.description && <div className="text-xs mb-4 leading-relaxed flex-1 font-light" style={{ color: t.soft }}>{g.description}</div>}
                      {g.type === "cash" ? (
                        <div className="mb-4">
                          {g.show_amounts !== false && <div className="flex justify-between text-[11px] mb-2" style={{ color: t.soft }}>
                            <span>{g.collected_amount || 0}€ collectés</span>
                            <span>Objectif {g.target_amount || 0}€</span>
                          </div>}
                          <div className="h-1 rounded-full overflow-hidden bg-stone-100">
                            <div className="h-1 rounded-full" style={{ background: t.accent, width: `${g.progress_percent ?? Math.min(100, ((g.collected_amount || 0) / Math.max(1, g.target_amount || 1)) * 100)}%` }} />
                          </div>
                        </div>
                      ) : (
                        <div className="text-lg mb-4 font-semibold" style={{ color: t.accent }}>{g.price ? `${g.price}€` : ""}</div>
                      )}
                      {g.claimed_by ? (
                        <div className="text-xs uppercase tracking-widest text-center py-3 bg-stone-50 rounded-xl" style={{ color: t.soft }}>✓ Réservé par {g.claimed_by}</div>
                      ) : (
                        <button onClick={() => claimGift(g)} className="w-full py-3 text-xs uppercase tracking-[0.2em] transition-colors rounded-xl font-bold" style={{ background: t.accent, color: "#fff" }} data-testid={`public-claim-${g.id}`}>
                          {g.type === "cash" ? "Contribuer" : "Réserver"}
                        </button>
                      )}
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING */}
      <section id="closing" className="relative h-[80vh] flex items-center justify-center text-center overflow-hidden" data-testid="section-closing">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center", filter: "brightness(0.65)" }} />
        <div className="absolute inset-0 bg-black/20" />
        <FadeIn className="relative z-10 text-white px-6">
          <div className="text-[10px] uppercase tracking-[0.4em] mb-6 opacity-85">Et maintenant</div>
          <h2 style={{ fontFamily: t.headingFont }} className="text-6xl lg:text-8xl font-light italic mb-8">On vous attend.</h2>
          {rsvpToken ? (
            <div className="mt-8">
              <a href={`/rsvp/${rsvpToken}`} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-black rounded-full text-sm uppercase tracking-widest hover:bg-opacity-90 transition-all font-semibold shadow-2xl">
                Confirmer ma présence
              </a>
            </div>
          ) : (
            <p className="text-sm max-w-md mx-auto opacity-90 leading-relaxed font-light">Pour répondre au RSVP, utilisez le lien unique reçu par e-mail ou SMS.</p>
          )}
          {wedding.hashtag && <div className="mt-16 text-2xl lg:text-3xl tracking-wider opacity-90" style={{ fontFamily: t.headingFont }}>#{wedding.hashtag}</div>}
        </FadeIn>
      </section>

      <footer className="px-6 py-12 text-center text-[10px] uppercase tracking-[0.3em] flex flex-col items-center" style={{ background: t.bg, color: t.soft, borderTop: `1px solid ${t.border}` }}>
        {publicSessionUnlocked && <button type="button" onClick={logoutPublicSession} className="mb-5 underline" data-testid="cloudpage-logout">Quitter l’accès privé</button>}
        {isSocialVisible && (wedding?.instagram || wedding?.pinterest) && (
            <div className="flex justify-center gap-6 mb-6">
                {wedding?.instagram && (
                    <a href={wedding.instagram} target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity">
                        Instagram
                    </a>
                )}
                {wedding?.pinterest && (
                    <a href={wedding.pinterest} target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity">
                        Pinterest
                    </a>
                )}
            </div>
        )}
        <div>Site créé avec <span style={{ color: t.accent, fontFamily: t.headingFont, textTransform: "none", letterSpacing: "0", fontSize: "14px" }} className="font-semibold">Ceremind</span></div>
      </footer>
    </div>
  );
}
